import { format } from 'date-fns';
import { useCallback, useMemo } from 'react';

import { ChipGroup } from '../../chip-group/chip-group';
import { DropdownSelect } from '../../dropdown-select/dropdown-select';
import { FilterChip } from '../../filter-chip/filter-chip';
import { Text } from '../../text/text';
import type {
  UseDateTimeBlockRenderTimesPropsInterface,
  UseDateTimeBlockRenderTimesReturnInterface,
  UseDateTimeBlockRenderTimesTimePropsInterface,
} from '../types/use-render-times.types';

/**
 * Hook for rendering times components (chips and select) with controlled behavior
 *
 * @param props - Configuration for the times components
 * @returns Rendered times components
 */
export function useDateTimeBlockRenderTimes(
  props: UseDateTimeBlockRenderTimesPropsInterface,
): UseDateTimeBlockRenderTimesReturnInterface {
  const {
    times,
    selectedTime,
    onTimeChange,
    isTimeDisabled,
    formFieldContext,
    multiple = false,
    renderTimesChips,
    renderTimesSelect,
    renderTimesHelp,
  } = props;

  /* Create time items with all necessary data */
  const timeItems = useMemo((): Array<UseDateTimeBlockRenderTimesTimePropsInterface> => {
    if (!times) return [];

    return times.map((time, index) => {
      const timeObj = time instanceof Date ? time : new Date(time);
      return {
        time,
        'value': time instanceof Date ? time.getTime().toString() : time.toString(),
        'label': format(timeObj, "h:mmaaaaa'm'"),
        'aria-label': format(timeObj, timeObj.getMinutes() === 0 ? 'h a' : 'h:mm a'),
        'disabled': isTimeDisabled(time),
        index,
      };
    });
  }, [times, isTimeDisabled]);

  /* Convert selected time(s) to string(s) for chips and select */
  const selectedTimeValue = useMemo(() => {
    if (!selectedTime) {
      return multiple ? ['default'] : 'default';
    }

    if (multiple && Array.isArray(selectedTime)) {
      if (selectedTime.length === 0) {
        return ['default'];
      }
      return selectedTime.map((time) =>
        time instanceof Date ? time.getTime().toString() : time.toString(),
      );
    }

    if (Array.isArray(selectedTime)) {
      /* Single mode but received array - take first item */
      const firstTime = selectedTime[0];
      if (!firstTime) {
        return multiple ? ['default'] : 'default';
      }
      return firstTime instanceof Date ? firstTime.getTime().toString() : firstTime.toString();
    }

    return selectedTime instanceof Date
      ? selectedTime.getTime().toString()
      : selectedTime.toString();
  }, [selectedTime, multiple]);

  /* Create options for DropdownSelect */
  const selectOptions = useMemo(() => {
    const defaultOption = {
      value: 'default',
      label: 'Available times',
      disabled: false,
    };
    const timeOptions = timeItems.map((item) => ({
      'value': item.value,
      'label': item.label,
      'aria-label': item['aria-label'],
      'disabled': item.disabled,
    }));
    return [defaultOption, ...timeOptions];
  }, [timeItems]);

  /* Handle chips selection change */
  const handleChipGroupSelection = useCallback(
    (value: string | Array<string> | undefined) => {
      if (!onTimeChange || !times || value === undefined) return;

      if (multiple && Array.isArray(value)) {
        /* Multiple selection mode - convert string values back to original time objects */
        const selectedTimes = value
          .map((stringValue) => {
            const timeIndex = times.findIndex((time) => {
              const timeValue = time instanceof Date ? time.getTime().toString() : time.toString();
              return timeValue === stringValue;
            });
            return timeIndex !== -1 ? times[timeIndex] : null;
          })
          .filter((time): time is Date | string => time !== null);

        onTimeChange(selectedTimes);
      } else if (typeof value === 'string') {
        /* Single selection mode */
        const timeIndex = times.findIndex((time) => {
          const timeValue = time instanceof Date ? time.getTime().toString() : time.toString();
          return timeValue === value;
        });
        if (timeIndex !== -1) {
          onTimeChange(times[timeIndex]);
        }
      }
    },
    [onTimeChange, times, multiple],
  );

  /* Handle select change */
  const handleSelectChange = useCallback(
    (value: string | Array<string>) => {
      if (!onTimeChange || !times) return;

      if (multiple && Array.isArray(value)) {
        /* Multiple selection mode - convert string values back to original time objects */
        const selectedTimes = value
          .filter((stringValue) => stringValue !== 'default')
          .map((stringValue) => {
            const timeIndex = times.findIndex((time) => {
              const timeValue = time instanceof Date ? time.getTime().toString() : time.toString();
              return timeValue === stringValue;
            });
            return timeIndex !== -1 ? times[timeIndex] : null;
          })
          .filter((time): time is Date | string => time !== null);

        onTimeChange(selectedTimes);
      } else if (typeof value === 'string' && value !== 'default') {
        /* Single selection mode */
        const timeIndex = times.findIndex((time) => {
          const timeValue = time instanceof Date ? time.getTime().toString() : time.toString();
          return timeValue === value;
        });
        if (timeIndex !== -1) {
          onTimeChange(times[timeIndex]);
        }
      }
    },
    [onTimeChange, times, multiple],
  );

  return useMemo(() => {
    const chipsElement =
      !times || times.length === 0 ? null : renderTimesChips ? (
        renderTimesChips({
          selectedValue: selectedTimeValue,
          onSelectionChange: handleChipGroupSelection,
          timeItems,
          formFieldContext,
          multiple,
        })
      ) : (
        <ChipGroup
          aria-label="Time selection"
          selected={selectedTimeValue}
          onSelectionChange={handleChipGroupSelection}
          multiple={multiple}
        >
          {timeItems.map((item) => (
            <FilterChip
              key={item.index}
              aria-label={item['aria-label']}
              value={item.value}
              disabled={item.disabled}
            >
              {item.label}
            </FilterChip>
          ))}
        </ChipGroup>
      );

    const selectElement =
      !times || times.length === 0 ? null : renderTimesSelect ? (
        renderTimesSelect({
          selectedValue: selectedTimeValue,
          onChange: handleSelectChange,
          options: selectOptions,
          formFieldContext,
          multiple,
        })
      ) : (
        <DropdownSelect
          aria-label="Time selection"
          options={selectOptions}
          value={selectedTimeValue}
          onChange={handleSelectChange}
          showLabelForValue
        />
      );

    const helpElement = renderTimesHelp ? (
      renderTimesHelp({
        formFieldContext,
      })
    ) : (
      <Text id={formFieldContext.descriptionId} textStyle="body-sm">
        Select a date to see available times
      </Text>
    );

    return {
      chipsElement,
      selectElement,
      helpElement,
    };
  }, [
    formFieldContext,
    handleChipGroupSelection,
    handleSelectChange,
    multiple,
    renderTimesChips,
    renderTimesHelp,
    renderTimesSelect,
    selectOptions,
    selectedTimeValue,
    timeItems,
    times,
  ]);
}
